import type { Account } from '../types';

/**
 * Which accounts belong to the same customer.
 *
 * Four copies of this test existed -- in the OTS modal, the WhatsApp offer
 * modal, the collect-payment modal and applyOTS -- and they did not agree.
 * One matched on customer name, another did not; one matched on mobile only
 * when it was exactly ten digits. So a customer could be "multi-account" on
 * one screen and single-account on the next, and the totals differed with it.
 */

/**
 * Placeholders that imported sheets use for "no value". Treating them as
 * real values is what merged 64 unrelated accounts into one customer: the
 * mobile column held the text "[NULL]", stripping non-digits left an empty
 * string on BOTH sides, and empty matched empty.
 */
const BLANKS = new Set(['', 'null', '[null]', 'n/a', 'na', '-', '--', 'nil', 'none', 'undefined']);

function isBlank(value?: string): boolean {
  return BLANKS.has((value || '').trim().toLowerCase());
}

function digits(value?: string): string {
  if (isBlank(value)) return '';
  return (value || '').replace(/\D/g, '');
}

function norm(value?: string): string {
  if (isBlank(value)) return '';
  return (value || '').trim().toLowerCase();
}

function balanceOf(a: Account): number {
  return Math.round(a.originalOutstandingAmount || a.outstandingAmount || 0);
}

/**
 * Every account belonging to the same customer as `account`, including
 * `account` itself. Matching is by customer code first (the bank's own
 * identifier and the most reliable), then by mobile number, then by CIF.
 *
 * Customer NAME is deliberately not a match on its own -- two different
 * borrowers can share a common name, and merging their balances would put
 * one customer's debt into another's settlement offer.
 */
export function getLinkedAccounts(
  account: Account | null | undefined,
  allAccounts: Account[]
): Account[] {
  if (!account) return [];

  const code = norm(account.customerCode);
  const cif = norm((account as any).cifNumber || (account as any).customerId);
  const mobile = digits(account.mobile);

  const name = norm(account.customerName);
  const balance = balanceOf(account);

  const linked = (allAccounts || []).filter((a) => {
    if (!a) return false;
    if (a.accountId === account.accountId || a.id === account.id) return true;
    if (code && norm(a.customerCode) === code) return true;
    if (cif && norm((a as any).cifNumber || (a as any).customerId) === cif) return true;
    if (mobile && mobile.length >= 10 && digits(a.mobile) === mobile) return true;
    // Same name AND same balance. Neither alone is enough -- two borrowers
    // can share a name, and thousands of accounts can share a balance -- but
    // together they identify the duplicated rows a sheet import leaves behind.
    if (name && balance > 0 && norm(a.customerName) === name && balanceOf(a) === balance) {
      return true;
    }
    return false;
  });

  // The account itself is always present, even if it is not in the list.
  if (!linked.some((a) => a.accountId === account.accountId)) {
    linked.unshift(account);
  }
  return linked;
}

export function isMultiAccountCustomer(
  account: Account | null | undefined,
  allAccounts: Account[]
): boolean {
  return getLinkedAccounts(account, allAccounts).length > 1;
}

/** What the customer still owes across every one of their accounts. */
export function mergedOutstanding(list: Account[]): number {
  return (list || []).reduce(
    (sum, a) => sum + Math.max(0, a.originalOutstandingAmount || a.outstandingAmount || 0),
    0
  );
}

/**
 * The settlement figure saved against an account, as a TOTAL.
 *
 * Note this is the agreed total, not what is left to pay -- see otsRemaining.
 */
export function otsTotalOf(account: Account | null | undefined): number {
  if (!account) return 0;
  return (
    account.finalOTSAmount ||
    account.userEnteredOTSAmount ||
    account.systemCalculatedOTSAmount ||
    account.otsAgreedAmount ||
    0
  );
}

/** How much has already been paid towards the settlement. */
export function otsPaidOf(account: Account | null | undefined): number {
  if (!account) return 0;
  const tracked = (account as any).otsPaidAmount;
  if (typeof tracked === 'number' && tracked > 0) return tracked;
  return account.ots10PercentPaidAmount || 0;
}

/** What is still to be collected under the settlement. */
export function otsRemaining(account: Account | null | undefined): number {
  const total = otsTotalOf(account);
  if (total <= 0) return 0;
  return Math.max(0, total - otsPaidOf(account));
}

/**
 * ONE ROW PER CUSTOMER, NOT ONE PER LOAN.
 *
 * A borrower with three loans produced three rows in every list, each one
 * repeating the same name, mobile and address, and each one's View opening
 * the SAME consolidated window with all three loans and an account selector
 * already inside it. The three rows carried no information the first did not.
 *
 * `getLinkedAccounts` answers this for ONE account by scanning the whole
 * list, which is fine for a modal but is a comparison of every account
 * against every other when a list does it per row -- some five million
 * comparisons on a 2,300-account book, on every render. This does it in one
 * pass instead, by the SAME rules, so a customer who is multi-account in the
 * OTS window cannot be two separate rows in the list behind it.
 *
 * Nothing here changes any stored figure. It is grouping for display.
 */
export interface CustomerGroup {
  /** The account whose row is drawn: the largest balance, so the row leads with the main exposure. */
  primary: Account;
  /** Every loan this borrower holds, the primary included, largest first. */
  loans: Account[];
  /** What the borrower owes across all of them. */
  totalOutstanding: number;
  totalOverdue: number;
  totalRecovered: number;
  loanCount: number;
}

export function groupAccountsByCustomer(accounts: Account[]): CustomerGroup[] {
  const list = (accounts || []).filter(Boolean);
  if (list.length === 0) return [];

  // Union-find: each account starts alone, then every shared identifier
  // joins two of them. One pass, no pairwise comparison.
  const parent = list.map((_, i) => i);
  const find = (i: number): number => {
    while (parent[i] !== i) {
      parent[i] = parent[parent[i]];
      i = parent[i];
    }
    return i;
  };
  const union = (a: number, b: number) => {
    const ra = find(a);
    const rb = find(b);
    if (ra !== rb) parent[rb] = ra;
  };

  // Same precedence as getLinkedAccounts, so the two cannot disagree.
  const seen = new Map<string, number>();
  const join = (key: string, index: number) => {
    const existing = seen.get(key);
    if (existing === undefined) seen.set(key, index);
    else union(existing, index);
  };

  list.forEach((a, i) => {
    const code = norm(a.customerCode);
    const cif = norm((a as any).cifNumber || (a as any).customerId);
    const mobile = digits(a.mobile);
    const name = norm(a.customerName);
    const balance = balanceOf(a);

    if (code) join(`code:${code}`, i);
    if (cif) join(`cif:${cif}`, i);
    if (mobile && mobile.length >= 10) join(`mob:${mobile}`, i);
    if (name && balance > 0) join(`nb:${name}|${balance}`, i);
  });

  const buckets = new Map<number, Account[]>();
  list.forEach((a, i) => {
    const root = find(i);
    const bucket = buckets.get(root);
    if (bucket) bucket.push(a);
    else buckets.set(root, [a]);
  });

  const groups: CustomerGroup[] = [];
  buckets.forEach((loans) => {
    const sorted = [...loans].sort((x, y) => balanceOf(y) - balanceOf(x));
    groups.push({
      primary: sorted[0],
      loans: sorted,
      totalOutstanding: sorted.reduce((s, a) => s + (Number(a.outstandingAmount) || 0), 0),
      totalOverdue: sorted.reduce((s, a) => s + (Number(a.overdueAmount) || 0), 0),
      totalRecovered: sorted.reduce((s, a) => s + (Number(a.totalRecovered) || 0), 0),
      loanCount: sorted.length,
    });
  });

  // The order the caller sorted the accounts in is preserved: a group takes
  // the position of its earliest member, so changing the sort still works.
  const firstIndex = new Map<Account, number>();
  list.forEach((a, i) => {
    if (!firstIndex.has(a)) firstIndex.set(a, i);
  });
  return groups.sort(
    (g1, g2) =>
      Math.min(...g1.loans.map((a) => firstIndex.get(a) ?? 0)) -
      Math.min(...g2.loans.map((a) => firstIndex.get(a) ?? 0))
  );
}
